import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  generateOfferAiSummary,
  listOfferAiSummaries,
  regenerateOfferAiSummary,
} from './offerAiSummaryApi';

// Loads the summary status for every offer on the listing transaction once so
// the per-offer button can show Generating / View / Regenerate without a
// request per card.
//
// The list call doubles as the access check: it only returns summaries the
// caller may view, so `enabled` means "feature on AND this viewer may use it".
//   404 (feature off) / 403 (not a primary party) → hidden.
//   Any other failure → `loadError`, so the UI can show an error state with
//   Retry rather than silently hiding.
//
// Mirrors useDocAiSummaries. Deliberately scoped per-transaction so switching
// transactions always refreshes.
const useOfferAiSummaries = (transactionId, { viewerIsPrimary = false } = {}) => {
  const [enabled, setEnabled] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [target, setTarget] = useState(null); // { offerId, summaryId }
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!transactionId) return;
    try {
      const list = await listOfferAiSummaries(transactionId);
      if (!mounted.current) return;
      setSummaries(list);
      setEnabled(true);
      setLoadError(false);
    } catch (err) {
      if (!mounted.current) return;
      const status = err?.response?.status;
      setEnabled(false);
      setSummaries([]);
      setLoadError(status !== 404 && status !== 403 && status !== 401);
    }
  }, [transactionId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  // Auto-poll the list endpoint when any summary is PENDING so the per-card
  // button flips from "Generating…" → "View summary" without a page refresh.
  //
  // Two throttles here:
  //  - Skip the poll entirely while the modal is open (`target` is set) —
  //    the modal runs its own 3-s by-id poll, so the list poll is redundant
  //    and chatty (~40 calls/min when both ran in parallel).
  //  - 10-s cadence instead of 3 s. Card-button flip tolerance is seconds,
  //    not sub-second; the modal still gets fast progress feedback.
  useEffect(() => {
    const anyPending = summaries.some((s) => s.status === 'PENDING');
    if (!anyPending || target) return undefined;
    const id = setInterval(() => { if (mounted.current) refresh(); }, 10000);
    return () => clearInterval(id);
  }, [summaries, refresh, target]);

  const byOfferId = useMemo(() => {
    const map = new Map();
    summaries.forEach((s) => map.set(s.offerId, s));
    return map;
  }, [summaries]);

  const summaryForOffer = useCallback((offerId) => (offerId ? byOfferId.get(offerId) || null : null), [byOfferId]);

  const open = useCallback((summaryId) => {
    setTarget({ summaryId });
  }, []);

  const close = useCallback(() => {
    setTarget(null);
    refresh();
  }, [refresh]);

  // User-initiated generation — fire the POST + refresh so the newly-created
  // PENDING record lands in `summaries`. Returns the summaryId so the caller
  // can open the progress modal immediately.
  const generate = useCallback(async (offerId) => {
    if (!transactionId || !offerId) return null;
    await generateOfferAiSummary(transactionId, offerId);
    const list = await listOfferAiSummaries(transactionId);
    if (mounted.current) {
      setSummaries(list);
      setEnabled(true);
      setLoadError(false);
    }
    const fresh = list.find((s) => s.offerId === offerId);
    return fresh ? fresh.id : null;
  }, [transactionId]);

  // User-initiated regenerate — called when the card's "Regenerate" button is
  // clicked on a FAILED summary. Fires the POST then refreshes the list so the
  // button flips back to Generating… and the caller can open the modal.
  const regenerate = useCallback(async (summaryId) => {
    if (!transactionId || !summaryId) return;
    await regenerateOfferAiSummary(transactionId, summaryId);
    const list = await listOfferAiSummaries(transactionId);
    if (mounted.current) {
      setSummaries(list);
      setEnabled(true);
      setLoadError(false);
    }
  }, [transactionId]);

  return {
    transactionId,
    enabled,
    loadError,
    viewerIsPrimary,
    summaryForOffer,
    refresh,
    target,
    open,
    close,
    generate,
    regenerate,
  };
};

export default useOfferAiSummaries;
